// ServicesMobile.jsx — onglet Services (mobile) en CONSULTATION.
// Les services portent une nomenclature de composants + une simulation de
// coût (cf. GestionServices.jsx) : l'édition complète reste sur desktop.
// Ici : recherche, filtres, et fiche détail en lecture.
import React, { useMemo, useState } from "react";
import apiClient from "../../api/apiClient";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import {
  ProductScreen, Appbar, Summary, SearchBar, Chips, EmptyState, Sheet,
  DetailRow, Note, Mono, Pill, Ico,
  useResourceList, useMobileMenu, fmtMoney,
} from "./productMobileKit";

const isActive = (s) => Number(s.is_active) === 1;
const gapOf = (s) => Number(s?.cost_preview?.pricing_gap || 0);
const costOf = (s) => Number(s?.cost_preview?.service_cost_total || 0);

export default function ServicesMobile({ onMenu: onMenuProp }) {
  const outletMenu = useMobileMenu();
  const onMenu = onMenuProp || outletMenu;
  const { hasPermission } = useAuth();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const canView = hasPermission("Gestion Commerciale.Services.view");

  const { rows: services, loading } = useResourceList(
    async () => {
      if (!selectedCompany || !canView) return [];
      const res = await apiClient.get("/api/services", {
        params: { company_id: selectedCompany, status: "all", page: 1, limit: 100, ...(selectedWarehouse ? { preview_warehouse_id: selectedWarehouse } : {}) },
      });
      return res.data?.services || [];
    },
    [selectedCompany, selectedWarehouse, canView]
  );

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);

  const active = useMemo(() => services.filter(isActive).length, [services]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return services.filter((s) => {
      if (filter === "active" && !isActive(s)) return false;
      if (filter === "inactive" && isActive(s)) return false;
      if (q && ![s.name, s.code].some((v) => String(v || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [services, query, filter]);

  return (
    <ProductScreen>
      <Appbar title="Services" onMenu={onMenu}
        subtitle={`${services.length} service${services.length > 1 ? "s" : ""} · ${active} actif${active > 1 ? "s" : ""}`}
        action={<button className="iconbtn"><Ico name="wrench" /></button>} />
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0, gap: 12 }}>
        <Summary items={[
          { label: "Services", value: services.length },
          { label: "Actifs", value: active },
          { label: "Inactifs", value: services.length - active },
        ]} />
        <SearchBar value={query} onChange={setQuery} placeholder="Rechercher un service…" />
        <Chips value={filter} onChange={setFilter} options={[
          { key: "all", label: "Tous" }, { key: "active", label: "Actifs" }, { key: "inactive", label: "Inactifs" },
        ]} />
      </div>

      <div className="body">
        {loading || !visible.length ? (
          <EmptyState icon="wrench" loading={loading}
            title={loading ? "Chargement des services" : (query || filter !== "all" ? "Aucun résultat" : "Aucun service")}
            text={loading ? "Synchronisation en cours." : "Créez vos services depuis l’ordinateur (composants + coûts)."} />
        ) : (
          <div className="list">
            {visible.map((s) => (
              <button key={s.id} className="rcard" onClick={() => setDetail(s)}>
                <Mono name={s.name} square />
                <div className="rcard__main">
                  <div className="rcard__name">{s.name}</div>
                  <div className="rcard__meta">
                    <Pill tone="info" label={fmtMoney(s.base_price)} />
                    {s.cost_preview ? <Pill tone={gapOf(s) < 0 ? "danger" : "ok"} label={`Marge ${fmtMoney(gapOf(s))}`} /> : null}
                    <Pill tone={isActive(s) ? "ok" : "off"} label={isActive(s) ? "Actif" : "Inactif"} />
                  </div>
                </div>
                <span className="rcard__chev"><Ico name="chevron-right" /></span>
              </button>
            ))}
          </div>
        )}
      </div>

      <Sheet open={!!detail} onClose={() => setDetail(null)} title={detail?.name} subtitle={detail?.code || "Service"}>
        {detail ? (
          <>
            <DetailRow label="Tarif de base" value={fmtMoney(detail.base_price)} />
            <DetailRow label="Coût estimé" value={fmtMoney(costOf(detail))} />
            <DetailRow label="Marge estimée" value={<span style={{ color: gapOf(detail) < 0 ? "var(--red)" : "var(--green)" }}>{fmtMoney(gapOf(detail))}</span>} />
            <DetailRow label="Composants" value={Number(detail.components?.length || 0)} />
            <DetailRow label="Produit facturé" value={detail.product_name || "Non défini"} />
            <DetailRow label="Établissement" value={detail.warehouse_name || "Global entreprise"} />
            <DetailRow label="Statut" value={<Pill tone={isActive(detail) ? "ok" : "off"} label={isActive(detail) ? "Actif" : "Inactif"} />} />
            <Note>La création et l’édition des services (composants, simulation de coût) se font sur la version ordinateur.</Note>
          </>
        ) : null}
      </Sheet>
    </ProductScreen>
  );
}
